import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  Suspense,
} from 'react';

import { LTLoader } from '@lambdatestincprivate/lt-components';
import styled from 'styled-components';

import './CustomControls.css';
import CustomVideoControls from './CustomControls';

import {
  sendAmplitudeEvents,
  updateAriaLive,
} from '../../utils/utilityFunctions';
import { EventsMap } from '../../constants/events';

import PlayIcon from '../../assets/videoPlay';

const ReactPlayer = React.lazy(() => import('react-player'));

const VIDEO_PARENT_CLASS = 'videoParent';

const CONTROLS_HEIGHT = 36;
const PROGRESS_INTERVAL = 300;

const PlayerOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  height: 100%;
  width: 100%;
`;

const IconOverlay = styled.span`
  height: 56px;
  width: 56px;
  background-color: rgba(255, 255, 255, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  padding-left: 5px;
  &:focus-visible {
    outline: 2px solid #2f81f7;
  }
`;

const CustomVideoPlayer = forwardRef((props, ref) => {
  const {
    defaultPlay,
    defaultRotation = 0,
    handleVideoDownload,
    height,
    onPlay,
    onPause,
    onProgressChange,
    onDurationChange,
    onPlaybackRateChange,
    onReady,
    onError,
    onBuffer,
    onBufferEnd,
    onFullScreenChange,
    progressInterval,
    controlContainerStyles,
    playerContainerStyles = {},
    showOverlay,
    mainContainerStyles,
    playbackRate: _,
    isLtAccessibilityEnabled = false,
    allowRotation = false,
    controllerWidth = '100%',
    onManualVideoProgress = () => {},
    disablePreview = false,
    opaqueLoader = false,
    isBackgroundShadow = false,
    setIsVideoPlaying,
    ...restProps
  } = props;

  const [isFirstPlay, setIsFirstPlay] = useState(false);
  const [isVideoLoadedAnnounced, setIsVideoLoadedAnnounced] = useState(false);

  const videoRef = ref || useRef(null);

  const [playerState, setPlayerState] = useState({
    isPlaying: false,
    progress: 0,
    bufferedProgress: 0,
    playbackRate: 1,
    ready: false,
    duration: 0,
    loading: false,
  });

  useEffect(() => {
    if (typeof setIsVideoPlaying === 'function') {
      setIsVideoPlaying(playerState.isPlaying);
    }
  }, [playerState.isPlaying]);

  useEffect(() => {
    // in case of live interaction video duration changes continuously as we get more video chunks
    if (isVideoLoadedAnnounced) {
      return;
    }
    if (!playerState.duration) {
      updateAriaLive('Loading Video...');
    } else {
      setIsVideoLoadedAnnounced(true);
      updateAriaLive('Video Loaded');
    }
  }, [playerState.duration]);

  useEffect(() => {
    const internalPlayer = getVideoInternalPlayer();
    if (internalPlayer) {
      setPlayerState((prevState) => ({
        ...prevState,
        isPlaying: defaultPlay,
      }));
      if (defaultPlay) {
        if (!isFirstPlay) {
          setIsFirstPlay(true);
        }
        internalPlayer.play();
      } else {
        internalPlayer.pause();
      }
    }
  }, [videoRef.current?.getInternalPlayer?.(), defaultPlay]);

  const [rotation, setRotation] = useState(defaultRotation);
  const [videoHeight, setVideoHeight] = useState(height);
  const [showControls, setShowControls] = useState(false);

  const handleRotation = (direction) => {
    let rotationValue = rotation % 360;
    if (direction === 'left') {
      rotationValue -= 90;
    } else if (direction === 'right') {
      rotationValue += 90;
    }
    setRotation(rotationValue);
    if (Math.abs(rotationValue) === 90 || Math.abs(rotationValue) === 270) {
      const parent = document?.querySelector(`.${VIDEO_PARENT_CLASS}`);
      const parentWidth = parent?.getBoundingClientRect().width;
      const parentHeight = parent?.getBoundingClientRect().height;
      setVideoHeight(`${parentWidth}px`);
      if (videoRef.current?.wrapper) {
        videoRef.current.wrapper.style.maxWidth = parentHeight + 'px';
      }
    } else {
      setVideoHeight('100%');
      if (videoRef.current?.wrapper) {
        videoRef.current.wrapper.style.maxWidth = '100%';
      }
    }
  };

  const getVideoInternalPlayer = () => {
    return videoRef.current?.getInternalPlayer?.();
  };

  const handlePlay = (e) => {
    setPlayerState((prevState) => ({
      ...prevState,
      isPlaying: true,
    }));
    if (typeof onPlay === 'function') {
      onPlay(e);
    }
  };

  const handlePause = (e) => {
    setPlayerState((prevState) => ({
      ...prevState,
      isPlaying: false,
    }));
    if (typeof onPause === 'function') {
      onPause(e);
    }
  };

  const handleVideoProgress = (progress) => {
    const playedSeconds = progress?.playedSeconds;
    const loadedSeconds = progress?.loadedSeconds;
    const currentVideoDuration = videoRef.current?.getDuration?.();

    // update the total duration in cases such as live video
    if (currentVideoDuration !== playerState?.duration) {
      setPlayerState((prevState) => ({
        ...prevState,
        duration: currentVideoDuration,
      }));
    }
    if (!playedSeconds) {
      return;
    }
    setPlayerState((prevState) => ({
      ...prevState,
      progress: playedSeconds,
      bufferedProgress: loadedSeconds,
    }));
    if (typeof onProgressChange === 'function') {
      onProgressChange(progress);
    }
  };

  const handleReady = (e) => {
    setPlayerState((prevState) => ({
      ...prevState,
      ready: true,
    }));
    if (typeof onReady === 'function') {
      onReady(e);
    }

    const player = videoRef?.current?.getInternalPlayer();
    if (player) {
      player.addEventListener('fullscreenchange', handleFullScreenChange);
      player.addEventListener('webkitfullscreenchange', handleFullScreenChange); // For Safari

      // Add empty captions track for WCAG 1.2.2 compliance
      // These are automated test recordings with no spoken audio
      if (!player.querySelector('track[kind="captions"]')) {
        const track = document.createElement('track');
        track.kind = 'captions';
        track.label = 'Captions';
        track.srclang = 'en';
        track.default = false;
        player.appendChild(track);
      }
    }
  };

  const handleError = (e) => {
    if (typeof onError === 'function') {
      onError(e);
    }
  };

  const handleDuration = (duration) => {
    setPlayerState((prevState) => ({
      ...prevState,
      duration,
    }));
    if (typeof onDurationChange === 'function') {
      onDurationChange(duration);
    }
  };

  const handlePlaybackRateChange = (playbackRate) => {
    setPlayerState((prevState) => ({
      ...prevState,
      playbackRate: playbackRate,
    }));
    if (typeof onPlaybackRateChange === 'function') {
      onPlaybackRateChange(playbackRate);
    }
  };

  const handleBuffer = (e) => {
    setPlayerState((prevState) => ({
      ...prevState,
      loading: true,
    }));
    if (typeof onBuffer === 'function') {
      onBuffer(e);
    }
  };

  const handleBufferEnd = (e) => {
    setPlayerState((prevState) => ({
      ...prevState,
      loading: false,
    }));
    if (typeof onBufferEnd === 'function') {
      onBufferEnd(e);
    }
  };

  const togglePlay = () => {
    const internalPlayer = videoRef.current?.getInternalPlayer?.();
    if (playerState.isPlaying) {
      internalPlayer.pause();
    } else {
      if (!isFirstPlay) {
        sendAmplitudeEvents(EventsMap.TEST_DETAILS_VIEW_VIDEO_PLAYED);
        setIsFirstPlay(true);
      }
      internalPlayer.play();
    }
  };

  const updatePlaybackRate = (playbackRate) => {
    setPlayerState((prevState) => ({
      ...prevState,
      playbackRate: playbackRate,
    }));
  };

  const handleFullScreenChange = () => {
    // Adding this check to show controls in Firefox for full screen mode
    const player = videoRef?.current?.getInternalPlayer();
    if (!player) {
      return;
    }
    const isFullScreen = !!(
      document?.fullscreenElement === player ||
      document?.webkitFullscreenElement === player
    );
    setShowControls(isFullScreen);

    if (typeof onFullScreenChange === 'function') {
      onFullScreenChange(isFullScreen);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        togglePlay();
      }
    };
    const handleClick = () => {
      // full screen toggling is handled internally by video player
      if (!document.fullscreenElement) {
        togglePlay();
      }
    };

    if (videoRef.current) {
      const wrapper = videoRef.current.wrapper;
      if (wrapper) {
        wrapper.addEventListener('keydown', handleKeyDown);
        wrapper.addEventListener('click', handleClick);
      }
    }

    return () => {
      if (videoRef.current) {
        const wrapper = videoRef.current.wrapper;
        const player = videoRef?.current?.getInternalPlayer();

        if (wrapper) {
          wrapper.removeEventListener('keydown', handleKeyDown);
          wrapper.removeEventListener('click', handleClick);
        }

        if (player) {
          player.removeEventListener(
            'fullscreenchange',
            handleFullScreenChange
          );
          player.removeEventListener(
            'webkitfullscreenchange',
            handleFullScreenChange
          ); // For Safari
        }
      }
    };
  }, [playerState.isPlaying]);

  return (
    <div
      className="ltp"
      style={{
        position: 'relative',
        ...mainContainerStyles,
      }}
    >
      {!playerState.duration && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
            ...(opaqueLoader
              ? { backgroundColor: '#fff', height: '100%', width: '100%' }
              : {}),
          }}
        >
          <LTLoader heading="Loading Video..." />
        </div>
      )}
      <div
        className={VIDEO_PARENT_CLASS}
        style={{
          display: 'flex',
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          minHeight: 0,
          width: '100%',
          position: 'relative',
          ...((rotation / 90) % 2 === 0 ? {} : { aspectRatio: '1/1' }),
        }}
      >
        {playerState.duration !== 0 && !isFirstPlay && !disablePreview && (
          <PlayerOverlay
            style={{
              transform: `rotate(${-rotation}deg)`, // Counteract rotation for the play icon
              backgroundColor: isLtAccessibilityEnabled ? '#212121' : '#fff',
            }}
          >
            <IconOverlay
              aria-label="Play Video"
              data-testid="video-play-overlay-btn"
              onClick={() => {
                togglePlay();
                setIsFirstPlay(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  togglePlay();
                  setIsFirstPlay(true);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <PlayIcon />
            </IconOverlay>
          </PlayerOverlay>
        )}
        <Suspense fallback={<></>}>
          <ReactPlayer
            ref={videoRef}
            onPlay={handlePlay}
            onPause={handlePause}
            onProgress={handleVideoProgress}
            onReady={handleReady}
            onError={handleError}
            onDuration={handleDuration}
            onPlaybackRateChange={handlePlaybackRateChange}
            onBuffer={handleBuffer}
            onBufferEnd={handleBufferEnd}
            height={videoHeight}
            progressInterval={progressInterval || PROGRESS_INTERVAL}
            controls={showControls}
            playbackRate={playerState.playbackRate}
            style={{
              ...playerContainerStyles,
              transform: `rotate(${rotation}deg)`,

              ...{
                ...(isBackgroundShadow && playerState.duration
                  ? {
                      filter: 'drop-shadow(rgb(102, 102, 102) 10px 10px 6px)',
                    }
                  : {}),
              },
            }}
            {...restProps}
          />
        </Suspense>
      </div>
      {playerState.duration !== 0 && (
        <CustomVideoControls
          handleVideoDownload={handleVideoDownload}
          videoRef={videoRef}
          controls={{
            ...playerState,
            togglePlay,
            setPlayerState,
            updatePlaybackRate,
            handleRotation,
            allowRotation,
            width: controllerWidth,
          }}
          containerStyles={{
            height: CONTROLS_HEIGHT,
            ...controlContainerStyles,
          }}
          onManualVideoProgress={onManualVideoProgress}
        />
      )}
    </div>
  );
});

export default CustomVideoPlayer;
